/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_bridge.wire

import io.peekandpoke.klang.common.infra.KlangMap

/**
 * Hand-written JS-interop helpers used by the generated wire codec (see `:audio-wire-codec-ksp` and
 * KLANG-00103 (worklet codec ksp)). Kept hand-written because `dynamic`/`js(...)` interop has sharp edges
 * (e.g. `.also {}` on a `dynamic` doesn't bind `it`) — the generator emits straight-line calls to these.
 */

/** Fresh empty JS object. */
fun wireObj(): dynamic = js("({})")

/** Kotlin List → JS array, element-encoded. */
inline fun <T> wireEncodeList(list: List<T>, enc: (T) -> dynamic): dynamic {
    val arr: dynamic = js("([])")
    for (e in list) arr.push(enc(e))
    return arr
}

/** JS array → Kotlin List, element-decoded. */
inline fun <T> wireDecodeList(arr: dynamic, dec: (dynamic) -> T): List<T> {
    val n: Int = arr.length.unsafeCast<Int>()
    val out = ArrayList<T>(n)
    var i = 0
    while (i < n) {
        out.add(dec(arr[i]))
        i++
    }
    return out
}

/** Kotlin Set → JS array, element-encoded. */
inline fun <T> wireEncodeSet(set: Set<T>, enc: (T) -> dynamic): dynamic {
    val arr: dynamic = js("([])")
    for (e in set) arr.push(enc(e))
    return arr
}

/** JS array → Kotlin Set, element-decoded (insertion order preserved via LinkedHashSet). */
inline fun <T> wireDecodeSet(arr: dynamic, dec: (dynamic) -> T): Set<T> {
    val n: Int = arr.length.unsafeCast<Int>()
    val out = LinkedHashSet<T>(n)
    var i = 0
    while (i < n) {
        out.add(dec(arr[i]))
        i++
    }
    return out
}

/**
 * [KlangMap] → JS object, walked with [KlangMap.forEachEntry] (nothing allocated per entry). The wire's one string-keyed
 * map shape: the generated codec refuses a stdlib `Map` field (`audio-wire-codec-ksp`).
 */
fun wireEncodeStringDoubleMap(m: KlangMap<Double>): dynamic {
    val o = wireObj()

    m.forEachEntry { k, v -> o[k] = v }

    return o
}

/**
 * JS object → a [KlangMap] (insertion order preserved via Object.keys). The engine reads these maps by key
 * (`ignitorParams` at every voice build, `katalystParams` at every owner change of an orbit), and on Kotlin/JS a
 * `HashMap` lookup allocates 40 bytes inside the stdlib's hashing, a `KlangMap` lookup nothing (engine follow-up 2,
 * KLANG-00290 (engine follow ups) item 2). The allocation check's `C-remap` case guards it.
 */
fun wireDecodeStringDoubleMap(o: dynamic): KlangMap<Double> {
    val keys = js("Object.keys(o)").unsafeCast<Array<String>>()
    val out = KlangMap<Double>()

    for (k in keys) {
        out[k] = o[k].unsafeCast<Double>()
    }

    return out
}

// ── Identity across the wire (`@WireShared`) ───────────────────────────────────────────────────────────────────

/** How many root codec calls are running now; the identity table lives while this is above 0. */
@PublishedApi
internal var wireScopeDepth: Int = 0

/**
 * The identity table of the running root call, a JS `Map`, or null until a `@WireShared` value is met. One call runs
 * one direction, so one table serves both: Kotlin instance to JS object while encoding, JS object to Kotlin instance
 * while decoding. A JS `Map` compares keys by identity, never by `equals`.
 */
@PublishedApi
internal var wireSeen: dynamic = null

/**
 * Runs the codec of a `@WireFormat` root: opens the identity table's lifetime at the outermost call and drops the table
 * when it ends, so nothing is remembered from one message to the next.
 */
inline fun <T> wireScoped(block: () -> T): T {
    wireScopeDepth++

    try {
        return block()
    } finally {
        wireScopeDepth--

        if (wireScopeDepth == 0) {
            wireSeen = null
        }
    }
}

/** Encodes a `@WireShared` value once per root call: a second reference to [v] gets the same JS object. */
inline fun wireEncodeShared(v: Any, enc: () -> dynamic): dynamic = wireShared(key = v, make = enc)

/** Decodes a `@WireShared` value once per root call: a second reference to [o] gets the same Kotlin object. */
inline fun <T> wireDecodeShared(o: dynamic, dec: () -> T): T = wireShared(key = o, make = dec).unsafeCast<T>()

@PublishedApi
internal inline fun wireShared(key: Any?, make: () -> Any?): dynamic {
    if (wireScopeDepth == 0) {
        return make()
    }

    if (wireSeen == null) {
        wireSeen = js("new Map()")
    }

    val seen = wireSeen
    val hit = seen.get(key)

    if (jsTypeOf(hit) != "undefined") {
        return hit
    }

    val made = make()
    seen.set(key, made)

    return made
}
